import React, { useState, useEffect } from 'react';
import { ICurso, ICreateCurso, ICursoErrors, ComunidadAutonoma, COMUNIDADES_AUTONOMAS } from '../types';
import FormHeader from '../components/FormHeader';
import SnackBar from '../components/SnackBar';
import Modal from '../components/Modal';
import { cursoService } from '../services/cursoService';
import { apiUtils } from '../config/api';
import { sociedadStorage } from '../stores/sociedadStorage';
import { cursoStorage } from '../stores/cursoStorage';
import './curso-form.css';

/**
 * Parsea una fecha desde la API evitando problemas de zona horaria
 * @param dateString - String de fecha en formato ISO (ej: "2025-11-17T23:00:00.000Z")
 * @returns Date object con la fecha correcta en hora local
 * 
 * IMPORTANTE: La API devuelve fechas en UTC. Cuando se parsea, JavaScript las convierte
 * automáticamente a hora local. Por ejemplo:
 * - "2025-11-22T23:00:00.000Z" (UTC) → 2025-11-23 00:00:00 (GMT+1)
 * - Necesitamos extraer la fecha en hora local, no la fecha UTC
 */
const parseDateFromAPI = (dateString: string | null | undefined): Date => {
  if (!dateString) return new Date();

  // Parsear el string ISO completo (JavaScript lo convierte automáticamente a hora local)
  const dateInLocal = new Date(dateString);

  // Extraer año, mes y día de la fecha en hora local (no UTC)
  const year = dateInLocal.getFullYear();
  const month = dateInLocal.getMonth();
  const day = dateInLocal.getDate();

  // Crear una nueva fecha con esos valores en hora local (sin hora, solo fecha)
  return new Date(year, month, day);
};

/**
 * Formatea una fecha a formato YYYY-MM-DD sin conversión de zona horaria
 * @param date - Date object
 * @returns String en formato YYYY-MM-DD
 */
const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface CursoFormProps {
  sociedadId: number;
  onBack: () => void;
  onComplete: (curso: ICurso) => void;
  onError?: (error: string) => void;
  onNavigateToStart?: () => void;
}

/**
 * @fileoverview Componente de formulario para crear un curso
 * @description Permite al usuario crear un curso individual
 */

const CursoForm: React.FC<CursoFormProps> = ({
  sociedadId,
  onBack,
  onComplete,
  onError,
  onNavigateToStart
}) => {
  const [formData, setFormData] = useState<ICreateCurso>({
    nombre: '',
    expediente: '',
    administracionPublica: null,
    numAlumnos: 0,
    numHoras: null,
    fechaInicio: new Date(), // Se ajustará en useEffect si es necesario
    fechaFin: new Date(), // Se ajustará en useEffect si es necesario
    ccaa: ComunidadAutonoma.CASTILLA_Y_LEON,
    ubicacionCurso: null,
    practicas: null,
    modalidad: null,
    idSociedad: sociedadId,
  });

  const [errors, setErrors] = useState<ICursoErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<'success' | 'warning'>('success');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cursoId, setCursoId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Obtiene el ID de la sociedad del LocalStorage al cargar el componente
   * y carga los datos guardados del formulario si existen
   * Si hay un ID de curso guardado, carga los datos desde la API
   */
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      // Verificar si hay un ID guardado
      const storedId = cursoStorage.getCursoId();

      if (storedId) {
        try {
          // Cargar datos desde la API
          const response = await cursoService.obtenerCurso(storedId);

          if (response.success && response.data) {
            // La respuesta tiene la estructura: response.data.curso
            const data = response.data.curso || response.data;

            setCursoId(storedId);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Parsear fechas desde la API evitando problemas de zona horaria
            const fechaInicioParsed = parseDateFromAPI(data.fechaInicio);
            const fechaFinParsed = parseDateFromAPI(data.fechaFin);

            // Ajustar fechas si son anteriores a hoy
            const fechaInicio = fechaInicioParsed < today ? today : fechaInicioParsed;
            const fechaFin = fechaFinParsed < today ? today : fechaFinParsed;

            // Cargar datos del curso
            setFormData({
              nombre: data.nombre || '',
              expediente: data.expediente || '',
              administracionPublica: data.administracionPublica || null,
              numAlumnos: data.numAlumnos || 0,
              numHoras: data.numHoras || null,
              fechaInicio: fechaInicio,
              fechaFin: fechaFin,
              ccaa: data.ccaa || ComunidadAutonoma.CASTILLA_Y_LEON,
              ubicacionCurso: data.ubicacionCurso || null,
              practicas: data.practicas || null,
              modalidad: data.modalidad || null,
              idSociedad: data.idSociedad
            });

            // No guardar datos en localStorage cuando hay ID, solo el ID ya está guardado
            // Los datos se cargan siempre desde la API cuando hay ID

            setIsLoading(false);
            return;
          }
        } catch (error) {
          // Si hay error al cargar, continuar con el flujo normal
          cursoStorage.clearCursoId();
        }
      }

      // Flujo normal: cargar desde localStorage
      setIsLoading(false);
    };

    loadData();
  }, [sociedadId]);

  /**
   * Carga datos desde localStorage si no hay ID guardado
   */
  useEffect(() => {
    if (isLoading || cursoId) return;

    const storedSociedadId = sociedadStorage.getSociedadId();
    if (storedSociedadId && storedSociedadId !== sociedadId) {
      setFormData(prev => ({
        ...prev,
        idSociedad: storedSociedadId
      }));
    }

    // Cargar datos guardados del formulario si existen
    const storedCurso = cursoStorage.getCurso();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (storedCurso) {
      // Asegurar que las fechas no sean anteriores a hoy
      const fechaInicio = storedCurso.fechaInicio && storedCurso.fechaInicio < today
        ? today
        : storedCurso.fechaInicio;
      const fechaFin = storedCurso.fechaFin && storedCurso.fechaFin < today
        ? today
        : storedCurso.fechaFin;

      setFormData(prev => ({
        ...storedCurso,
        fechaInicio: fechaInicio || prev.fechaInicio,
        fechaFin: fechaFin || prev.fechaFin,
        idSociedad: storedSociedadId || prev.idSociedad
      }));
    } else {
      // Si no hay datos guardados, asegurar que las fechas iniciales no sean anteriores a hoy
      setFormData(prev => {
        const fechaInicio = prev.fechaInicio < today ? today : prev.fechaInicio;
        const fechaFin = prev.fechaFin < today ? today : prev.fechaFin;
        return {
          ...prev,
          fechaInicio,
          fechaFin
        };
      });
    }
  }, [sociedadId, isLoading, cursoId]);

  /**
   * Maneja cambios en los inputs y actualiza el estado del formulario
   * @param e - Evento de cambio del input
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    let newValue: any;
    if (type === 'number') {
      newValue = value === '' ? null : Number(value);
    } else if (name === 'ccaa') {
      newValue = value as ComunidadAutonoma;
    } else if (name === 'practicas' || name === 'modalidad') {
      // Para prácticas y modalidad, si está vacío, usar null
      newValue = value && value.trim() !== '' ? value : null;
    } else if (name === 'nombre' || name === 'administracionPublica' || name === 'ubicacionCurso') {
      // Para campos opcionales, si está vacío, usar null
      newValue = value;
    } else {
      newValue = value;
    }

    const updatedData = {
      ...formData,
      [name]: newValue
    };

    setFormData(updatedData);

    // Guardar en LocalStorage solo si NO hay ID (modo creación)
    // Si hay ID (modo edición), los datos se cargan desde la API
    if (!cursoId) {
      cursoStorage.saveCurso(updatedData);
    }

    // Clear error when user starts typing
    if (errors[name as keyof ICursoErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  /**
   * Maneja cambios en los inputs de fecha
   * @param e - Evento de cambio del input
   */
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Parsear la fecha desde el input (formato YYYY-MM-DD) evitando problemas de zona horaria
    // El input date siempre devuelve el formato YYYY-MM-DD
    const selectedDate = parseDateFromAPI(value);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Validar que la fecha no sea anterior a la fecha actual
    if (selectedDate < today) {
      setErrors(prev => ({
        ...prev,
        [name]: `La fecha ${name === 'fechaInicio' ? 'de inicio' : 'de fin'} no puede ser anterior a la fecha actual`
      }));
      return;
    }

    const updatedData = {
      ...formData,
      [name]: selectedDate
    };

    setFormData(updatedData);

    // Guardar en LocalStorage solo si NO hay ID (modo creación)
    // Si hay ID (modo edición), los datos se cargan desde la API
    if (!cursoId) {
      cursoStorage.saveCurso(updatedData);
    }

    // Clear error when user changes date
    if (errors[name as keyof ICursoErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  /**
   * Valida los datos del formulario
   * @returns boolean - true si la validación es exitosa
   */
  const validateForm = (): boolean => {
    const newErrors: ICursoErrors = {};

    // Validación de expediente
    if (!formData.expediente.trim()) {
      newErrors.expediente = 'El expediente es obligatorio';
    }

    // Validación de administración pública (opcional, no se valida)

    // Validación de número de alumnos
    if (formData.numAlumnos <= 0) {
      newErrors.numAlumnos = 'El número de alumnos debe ser mayor a 0';
    }

    // Validación de fechas
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!formData.fechaInicio) {
      newErrors.fechaInicio = 'La fecha de inicio es obligatoria';
    } else {
      if (formData.fechaInicio < today) {
        newErrors.fechaInicio = 'La fecha de inicio no puede ser anterior a la fecha actual';
      }
    }

    if (!formData.fechaFin) {
      newErrors.fechaFin = 'La fecha de fin es obligatoria';
    } else {
      if (formData.fechaFin < today) {
        newErrors.fechaFin = 'La fecha de fin no puede ser anterior a la fecha actual';
      } else if (formData.fechaInicio && formData.fechaFin <= formData.fechaInicio) {
        newErrors.fechaFin = 'La fecha de fin debe ser posterior a la fecha de inicio';
      }
    }

    // Validación de CCAA (obligatorio)
    if (!formData.ccaa) {
      newErrors.ccaa = 'La Comunidad Autónoma es obligatoria';
    }

    // Validación de ubicación (opcional, max 100 caracteres)
    if (formData.ubicacionCurso && formData.ubicacionCurso.length > 100) {
      newErrors.ubicacionCurso = 'La ubicación no puede exceder los 100 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Maneja el envío del formulario
   * @param e - Evento de envío del formulario
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        // Preparar datos para la API
        const apiData = {
          nombre: formData.nombre,
          expediente: formData.expediente,
          administracionPublica: formData.administracionPublica,
          numAlumnos: formData.numAlumnos,
          numHoras: formData.numHoras,
          fechaInicio: formatDateForAPI(formData.fechaInicio), // Formato YYYY-MM-DD sin conversión de zona horaria
          fechaFin: formatDateForAPI(formData.fechaFin), // Formato YYYY-MM-DD sin conversión de zona horaria
          ccaa: formData.ccaa,
          ubicacionCurso: formData.ubicacionCurso,
          practicas: formData.practicas,
          modalidad: formData.modalidad,
          idSociedad: formData.idSociedad
        };

        let response;
        let curso: ICurso;

        // Si hay un ID, actualizar; si no, crear
        if (cursoId) {
          response = await cursoService.actualizarCurso(cursoId, apiData);

          if (response.success) {
            setSuccessMessage(response.message || 'Curso actualizado exitosamente');
            setSnackbarType('warning');
            setShowSuccessSnackbar(true);

            // Crear objeto curso con datos actualizados
            curso = {
              idCurso: cursoId,
              ...formData
            };
          } else {
            setErrorMessage(response.message || 'Error al actualizar el curso');
            setShowErrorModal(true);
            return;
          }
        } else {
          // Crear nuevo curso
          response = await cursoService.crearCurso(apiData);

          if (response.success) {
            // La respuesta tiene la estructura: response.data.curso.idCurso
            const newId = response.data?.curso?.idCurso;

            if (newId) {
              // Guardar el ID en el store
              cursoStorage.saveCursoId(newId);
              setCursoId(newId);

              // Verificar que se guardó correctamente
              const storedId = cursoStorage.getCursoId();
            }

            setSuccessMessage(response.message || 'Curso creado exitosamente');
            setSnackbarType('success');
            setShowSuccessSnackbar(true);

            // Crear objeto curso con el ID devuelto por la API
            curso = {
              idCurso: newId || Math.floor(Math.random() * 1000),
              ...formData
            };
          } else {
            setErrorMessage(response.message || 'Error al crear el curso');
            setShowErrorModal(true);
            return;
          }
        }

        // No limpiar el store si estamos en modo edición, para permitir volver atrás
        if (!cursoId) {
          // Solo limpiar si es creación nueva y se completa el flujo
          // cursoStorage.clearCurso();
        }

        setTimeout(() => {
          onComplete(curso);
        }, 2000);

      } catch (error) {
        const errorMsg = apiUtils.handleError(error);
        setErrorMessage(errorMsg);
        if (onError) {
          onError(errorMsg);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="container">
      <div className="form-container">
        <FormHeader onLogoClick={onNavigateToStart} />
        <h2 className="form-subtitle">UN CURSO</h2>

        {cursoId && (
          <div className="update-mode-notice">
            <strong>Modo de actualización</strong>
            <p>
              Está actualizando un curso existente
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="nombre">
              Nombre del curso <span className="required">*</span>
            </label>
            <input
              type="text"
              id="nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              className={errors.nombre ? 'error' : ''}
              required
              aria-describedby={errors.nombre ? 'nombre-error' : undefined}
            />
            {errors.nombre && <span id="nombre-error" className="error-message">{errors.nombre}</span>}
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="expediente">
                Número de expediente <span className="required">*</span>
              </label>
              <input
                type="text"
                id="expediente"
                name="expediente"
                value={formData.expediente}
                onChange={handleInputChange}
                className={errors.expediente ? 'error' : ''}
                required
                aria-describedby={errors.expediente ? 'expediente-error' : undefined}
              />
              {errors.expediente && <span id="expediente-error" className="error-message">{errors.expediente}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="administracionPublica">
                Administración pública
              </label>
              <input
                type="text"
                id="administracionPublica"
                name="administracionPublica"
                value={formData.administracionPublica || ''}
                onChange={handleInputChange}
                className={errors.administracionPublica ? 'error' : ''}
                aria-describedby={errors.administracionPublica ? 'administracionPublica-error' : undefined}
              />
              {errors.administracionPublica && <span id="administracionPublica-error" className="error-message">{errors.administracionPublica}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="numAlumnos">
                Número de alumnos <span className="required">*</span>
              </label>
              <input
                type="number"
                id="numAlumnos"
                name="numAlumnos"
                value={formData.numAlumnos || ''}
                onChange={handleInputChange}
                className={errors.numAlumnos ? 'error' : ''}
                required
                min="1"
                aria-describedby={errors.numAlumnos ? 'numAlumnos-error' : undefined}
              />
              {errors.numAlumnos && <span id="numAlumnos-error" className="error-message">{errors.numAlumnos}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="numHoras">
                Número de horas
              </label>
              <input
                type="number"
                id="numHoras"
                name="numHoras"
                value={formData.numHoras || ''}
                onChange={handleInputChange}
                className={errors.numHoras ? 'error' : ''}
                aria-describedby={errors.numHoras ? 'numHoras-error' : undefined}
              />
              {errors.numHoras && <span id="numHoras-error" className="error-message">{errors.numHoras}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="fechaInicio">
                Fecha de inicio <span className="required">*</span>
              </label>
              <input
                type="date"
                id="fechaInicio"
                name="fechaInicio"
                value={formatDateForAPI(formData.fechaInicio)}
                onChange={handleDateChange}
                className={errors.fechaInicio ? 'error' : ''}
                required
                min={formatDateForAPI(new Date())}
                aria-describedby={errors.fechaInicio ? 'fechaInicio-error' : undefined}
              />
              {errors.fechaInicio && <span id="fechaInicio-error" className="error-message">{errors.fechaInicio}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="fechaFin">
                Fecha de fin <span className="required">*</span>
              </label>
              <input
                type="date"
                id="fechaFin"
                name="fechaFin"
                value={formatDateForAPI(formData.fechaFin)}
                onChange={handleDateChange}
                className={errors.fechaFin ? 'error' : ''}
                required
                min={formatDateForAPI(new Date())}
                aria-describedby={errors.fechaFin ? 'fechaFin-error' : undefined}
              />
              {errors.fechaFin && <span id="fechaFin-error" className="error-message">{errors.fechaFin}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="ccaa">
                Comunidad Autónoma donde se imparte la formación  <span className="required">*</span>
              </label>
              <select
                id="ccaa"
                name="ccaa"
                value={formData.ccaa}
                onChange={handleInputChange}
                className={errors.ccaa ? 'error' : ''}
                required
                aria-describedby={errors.ccaa ? 'ccaa-error' : undefined}
              >
                {COMUNIDADES_AUTONOMAS.map(ccaa => (
                  <option key={ccaa} value={ccaa}>{ccaa}</option>
                ))}
              </select>
              {errors.ccaa && <span id="ccaa-error" className="error-message">{errors.ccaa}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="ubicacionCurso">
                Ubicación del curso
              </label>
              <input
                type="text"
                id="ubicacionCurso"
                name="ubicacionCurso"
                value={formData.ubicacionCurso || ''}
                onChange={handleInputChange}
                className={errors.ubicacionCurso ? 'error' : ''}
                maxLength={100}
                aria-describedby={errors.ubicacionCurso ? 'ubicacionCurso-error' : undefined}
              />
              {errors.ubicacionCurso && <span id="ubicacionCurso-error" className="error-message">{errors.ubicacionCurso}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="practicas">Prácticas</label>
              <select
                id="practicas"
                name="practicas"
                value={formData.practicas || ''}
                onChange={handleInputChange}
              >
                <option value=""></option>
                <option value="No">No</option>
                <option value="Si">Sí</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="modalidad">Modalidad</label>
              <select
                id="modalidad"
                name="modalidad"
                value={formData.modalidad || ''}
                onChange={handleInputChange}
              >
                <option value=""></option>
                <option value="Presencial">Presencial</option>
                <option value="Teleformación">Teleformación</option>
              </select>
            </div>
          </div>

          <div className="button-group">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onNavigateToStart}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
            >
              Atrás
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting
                ? (cursoId ? 'Actualizando...' : 'Creando...')
                : (cursoId ? 'Actualizar Curso' : 'Crear Curso')
              }
            </button>
          </div>
        </form>

        {/* SnackBar para éxito */}
        <SnackBar
          isOpen={showSuccessSnackbar}
          onClose={() => setShowSuccessSnackbar(false)}
          message={successMessage}
          type={snackbarType}
          duration={4000}
          position="bottom-center"
        />

        {/* Modal para errores */}
        <Modal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title="Error al crear curso"
        >
          <div style={{ color: '#d32f2f', lineHeight: '1.5' }}>
            <p style={{ margin: '0 0 16px 0' }}>
              <strong>Error:</strong> {errorMessage}
            </p>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>
              Por favor, revise los datos e intente nuevamente.
            </p>
            <div style={{ textAlign: 'right' }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowErrorModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default CursoForm;
